<template>
  <ul
    :aria-controls="ariaControls"
    :aria-label="ariaLabel"
    class="pagination"
    :class="paginationClass">
    <li
      class="page-item"
      :class="[alignmentClasses, firstClass]"
      v-if="!hideGotoEndButtons">
      <slot
        name="first-text"
        :page="1"
        :index="0"
        :disabled="isFirstPage || disabled"
        :onClick="($event: Event) => handlePaginationInput($event, 1)">
        <button
          @click="($event) => handlePaginationInput($event, 1)"
          :aria-label="labelFirstPage"
          :disabled="isFirstPage || disabled"
          class="page-link"
          :class="[
            {
              disabled: disabled || isFirstPage,
            },
            buttonClasses,
          ]">
          <template v-if="!firstNumber">{{ firstText }}</template>
          <template v-if="firstNumber">1</template>
        </button>
      </slot>
    </li>
    <li class="page-item" :class="[alignmentClasses, prevClass]">
      <slot
        name="prev-text"
        :page="isFirstPage ? 1 : pageCount - 1"
        :index="isFirstPage ? 0 : pageCount - 2"
        :disabled="isFirstPage || disabled"
        :onClick="($event: Event) => handlePrevInput($event)">
        <button
          @click="handlePrevInput($event)"
          :disabled="isFirstPage || disabled"
          :aria-label="labelPrevPage"
          class="page-link"
          :class="[
            {
              disabled: disabled || isFirstPage,
            },
            buttonClasses,
          ]">
          {{ prevText }}
        </button>
      </slot>
    </li>
    <li
      class="page-item"
      :class="[alignmentClasses, ellipsisClass]"
      v-if="currentPage > buttonBuffer && !hideEllipsis">
      <button disabled class="page-link disabled" :class="[buttonClasses]">
        <slot name="ellipsis-text">{{ ellipsisText }}</slot>
      </button>
    </li>
    <template v-for="item in pageCount">
      <li
        :key="item"
        class="page-item"
        :class="[alignmentClasses, pageClass]"
        v-if="buttonIsVisible(item)">
        <slot
          name="page"
          :disabled="currentPage === item || disabled"
          :active="currentPage === item"
          :page="item"
          :index="pageCount - 1"
          :onClick="($event: Event) => handlePaginationInput($event, item)">
          <button
            :disabled="currentPage === item || disabled"
            :active="currentPage === item"
            :aria-label="`${labelPage} ${item}`"
            @click="($event: Event) => handlePaginationInput($event, item)"
            class="page-link"
            :class="[
              {
                active: currentPage === item,
                disabled: disabled,
              },
              buttonClasses,
            ]">
            {{ item }}
          </button>
        </slot>
      </li>
    </template>
    <li
      class="page-item"
      role="separator"
      :class="[alignmentClasses, ellipsisClass]"
      v-if="currentPage < pageCount - buttonBuffer + 1 && !hideEllipsis">
      <span class="page-link disabled" :class="[buttonClasses]">
        <slot name="ellipsis-text">{{ ellipsisText }}</slot>
      </span>
    </li>
    <li class="page-item" :class="[alignmentClasses, nextClass]">
      <slot
        name="next-text"
        :disabled="isLastPage || disabled"
        :page="pageCount >= currentPage + 1 ? currentPage + 1 : pageCount"
        :index="currentPage"
        :onClick="($event: Event) => handleNextInput($event)">
        <button
          @click="handleNextInput($event)"
          :aria-label="labelNextPage"
          :disabled="isLastPage || disabled"
          class="page-link"
          :class="[
            {
              disabled: disabled || isLastPage,
            },
            buttonClasses,
          ]">
          {{ nextText }}
        </button>
      </slot>
    </li>
    <li
      aria
      class="page-item"
      :class="[alignmentClasses, lastClass]"
      v-if="!hideGotoEndButtons">
      <slot
        name="last-text"
        :page="pageCount"
        :index="pageCount - 1"
        :disabled="isLastPage || disabled"
        :onClick="($event: Event) => handlePaginationInput($event, pageCount)">
        <button
          @click="($event) => handlePaginationInput($event, pageCount)"
          :disabled="isLastPage || disabled"
          :aria-label="labelLastPage"
          class="page-link"
          :class="[
            {
              disabled: disabled || isLastPage,
            },
            buttonClasses,
          ]">
          <template v-if="!lastNumber">{{ lastText }}</template>
          <template v-if="lastNumber">{{ pageCount }}</template>
        </button>
      </slot>
    </li>
  </ul>
</template>

<script setup lang="ts">
import { useOffsetPagination } from "@vueuse/core";
import { computed, ref, watch } from "vue";

interface ClassObj {
  [key: string]: string;
}

const ctaAlignMap = {
  left: "start",
  center: "center",
  right: "end",
  stretch: "stretch",
};

interface Props {
  align?: "left" | "right" | "center" | "stretch";
  ariaControls?: string;
  ariaLabel?: string;
  disabled?: boolean;
  hideEllipsis?: boolean;
  ellipsisClass?: [] | string | ClassObj;
  ellipsisText?: string;
  firstClass?: [] | string | ClassObj;
  firstText?: string;
  firstNumber?: boolean;
  hideGotoEndButtons?: boolean;
  labelFirstPage?: string;
  labelLastPage?: string;
  labelNextPage?: string;
  labelPage?: string;
  labelPrevPage?: string;
  lastClass?: [] | string | ClassObj;
  lastNumber?: boolean;
  lastText?: string;
  limit?: number;
  nextClass?: [] | string | ClassObj;
  nextText?: string;
  pageClass?: [] | string | ClassObj;
  perPage?: number;
  pills?: boolean;
  prevClass?: [] | string | ClassObj;
  prevText?: string;
  size?: "sm" | "md" | "lg";
  totalRows: number;
}

const props = withDefaults(defineProps<Props>(), {
  ariaLabel: "Pagination",
  ellipsisText: "...",
  firstText: "«",
  labelFirstPage: "Go to first page",
  labelLastPage: "Go to last page",
  labelNextPage: "Go to next page",
  labelPage: "Go to page",
  labelPrevPage: "Go to previous page",
  lastText: "»",
  limit: 5,
  nextText: "›",
  perPage: 20,
  prevText: "‹",
  totalRows: 0,
});

const emits = defineEmits<{
  /**
   *  emmitted on page change
   */
  (e: "change", page: number): void;
  /**
   *  emmitted on page change caused by input
   */
  (e: "input", page: number): void;
  /**
   *  emmitted when a user clicks the page buttons
   */
  (e: "page-click", event: Event, page: number): void;
}>();

// converting props to refs to make them reactive for useOffsetPagination
const perPageRef = ref(0);
watch(
  () => props.perPage,
  (newValue) => {
    perPageRef.value = newValue;
  },
  { immediate: true }
);

const totalRowsRef = ref(0);
watch(
  () => props.totalRows,
  (newValue) => {
    totalRowsRef.value = newValue;
  },
  { immediate: true }
);

const model = defineModel<number>({ required: true });

const { currentPage, pageCount, isFirstPage, isLastPage, prev, next } =
  useOffsetPagination({
    total: totalRowsRef,
    page: model.value,
    pageSize: perPageRef,
    onPageChange: updatePage,
  });

function updatePage({ currentPage }: { currentPage: number }) {
  model.value = currentPage;
  emits("change", currentPage);
}

const handlePaginationInput = (event: Event, page: number) => {
  currentPage.value = page;
  emits("input", page);
  emits("page-click", event, page);
};

const handlePrevInput = (event: Event) => {
  prev();
  emits("input", currentPage.value);
  emits("page-click", event, currentPage.value);
};

const handleNextInput = (event: Event) => {
  next();
  emits("input", currentPage.value);
  emits("page-click", event, currentPage.value);
};

// helps account for the ellipsis in the `limit` count
const buttonBuffer = computed(() => {
  let reservedSpaces = 1;

  if (
    props.hideEllipsis == false &&
    currentPage.value > 2 &&
    pageCount.value != props.limit
  ) {
    reservedSpaces = 3;
  }

  if (props.hideEllipsis == false && currentPage.value >= pageCount.value - 1) {
    reservedSpaces = 1;
  }

  if (pageCount.value == props.limit) {
    reservedSpaces = 0;
  }

  return props.limit - reservedSpaces;
});

const paginationClass = computed(() => {
  let classes = "";

  props.pills ? (classes += " b-pagination-pills ") : null;

  props.align && ["left", "right", "center", "stretch"].includes(props.align)
    ? (classes += ` justify-content-${ctaAlignMap[props.align]} `)
    : null;

  props.size == "sm" || props.size == "lg"
    ? (classes += ` pagination-${props.size}`)
    : null;

  return classes;
});

const alignmentClasses = computed(() => {
  return props.align == "stretch" ? `flex-grow-1` : ``;
});

const buttonClasses = computed(() => {
  return props.align == "stretch" ? `w-100` : ``;
});

// determines if the pagination button should be visible or not using the limit and if there are elips
const buttonIsVisible = (item: number) => {
  // accounts for the elips being there or not
  let elipsBuffer = 0;
  if (props.hideEllipsis == false && pageCount.value != props.limit) {
    elipsBuffer = 1;
  }

  // check for the lower range of numbers
  if (
    item <= props.limit - elipsBuffer &&
    currentPage.value <= buttonBuffer.value - 1
  ) {
    return true;
  }
  // check for the upper range of numbers
  else if (
    item > pageCount.value - props.limit + elipsBuffer &&
    item < Math.abs(buttonBuffer.value / 2 + currentPage.value)
  ) {
    return true;
  }
  // checks for the mid range of numbers
  else if (
    item <= Math.abs(buttonBuffer.value / 2 + currentPage.value) &&
    item >= Math.abs(buttonBuffer.value / 2 - currentPage.value)
  ) {
    return true;
  } else {
    return false;
  }
};

watch(model, (newValue) => {
  currentPage.value = newValue;
});
</script>

<style lang="scss" scoped>
.page-item {
  display: flex;
  justify-content: center;
  align-items: center;
}
.b-pagination-pills {
  .page-item {
    .page-link {
      border-radius: 50rem !important;
      margin-left: 0.25rem;
      line-height: 1;
    }

    &:first-child {
      .page-link {
        margin-left: 0;
      }
    }
  }
}
</style>
